import EmptyState from '@/components/EmptyState';
import { SettingIcon } from '@/components/SettingIcon';
import { usePrinters } from '@/features/print/hooks/usePrint';
import {
  NOTIFICATION_URL,
  PRINT_URL,
  copyRow,
} from '@/features/print/components/serverDirectPrint';
import type { Printer } from '@restaurant/api-client';
import {
  Badge,
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListItem,
  Navbar,
  NavRight,
  Link as F7Link,
  Page,
  Preloader,
} from 'framework7-react';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { useT } from '@/i18n';
import type { Translations } from '@/i18n/locales/en';

// The Print URL and Notification URL are the same for every Epson - they are the
// operator's Server Direct Print endpoints. Shown here once, always visible (the
// way In-House presents them), so they can be copied into a printer's setup even
// while adding the very first one, before any Printer ID exists.
function serverDirectPrintSection(tp: Translations['print']) {
  if (!PRINT_URL && !NOTIFICATION_URL) return null;
  return (
    <>
      <BlockTitle>{tp.serverDirectTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{tp.serverDirectFooter}</BlockFooter>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd mediaList>
        {PRINT_URL && copyRow(tp, tp.printUrlLabel, PRINT_URL)}
        {NOTIFICATION_URL && copyRow(tp, tp.notificationUrlLabel, NOTIFICATION_URL)}
      </List>
    </>
  );
}

function PrintersPage() {
  const t = useT();
  const STATUS_META: Record<Printer['status'], { label: string; color: string }> = {
    ONLINE: { label: t.print.statusOnline, color: 'green' },
    OFFLINE: { label: t.print.statusOffline, color: 'gray' },
    ERROR: { label: t.print.statusError, color: 'red' },
  };
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: printers, isLoading, error } = usePrinters();

  return (
    <Page name="printers">
      <Navbar title={t.settingsFeature.printers} backLink={settingsBackLink}>
        <NavRight>
          <F7Link iconF7="plus" href="/settings/printers/new" />
        </NavRight>
      </Navbar>

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title={t.print.couldntLoadPrinters}
          description={(error as Error).message}
        />
      )}

      {printers && printers.length === 0 && (
        <>
          <EmptyState
            icon="printer"
            title={t.print.noPrintersTitle}
            description={t.print.noPrintersDesc}
          />
          {serverDirectPrintSection(t.print)}
        </>
      )}

      {printers && printers.length > 0 && (
        <>
          <BlockTitle>{t.print.printersTitle}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{t.print.printersFooter}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd mediaList>
            {printers.map((p) => {
              const meta = STATUS_META[p.status];
              const subtitle =
                p.queuedCount > 0 ? t.print.queued(p.queuedCount) : t.print.noJobsQueued;
              return (
                <ListItem
                  key={p.id}
                  link={`/settings/printers/${p.id}`}
                  title={p.name}
                  subtitle={subtitle}
                >
                  <i slot="media" className="f7-icons" style={{ color: '#8e8e93' }}>
                    printer
                  </i>
                  <div slot="after" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {p.isDefault && <Badge color="blue">{t.print.defaultBadge}</Badge>}
                    <Badge color={meta.color}>{meta.label}</Badge>
                  </div>
                </ListItem>
              );
            })}
          </List>

          {serverDirectPrintSection(t.print)}
        </>
      )}

      <BlockTitle>{t.print.routingAndHistoryTitle}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={t.print.behaviourTitle} link="/settings/printers/behaviour">
          <div slot="media">
            <SettingIcon icon="slider_horizontal_3" color="#f59e0b" />
          </div>
        </ListItem>
        <ListItem title={t.print.routingTitle} link="/settings/printers/routing">
          <div slot="media">
            <SettingIcon icon="arrow_branch" color="#0d9488" />
          </div>
        </ListItem>
        <ListItem title={t.print.historyTitle} link="/settings/printers/jobs">
          <div slot="media">
            <SettingIcon icon="doc_text" color="#6366f1" />
          </div>
        </ListItem>
      </List>
    </Page>
  );
}

export default PrintersPage;
